import { BuyerSellerResponse } from "../../types";

/**
 * Pure "which suppliers are selected" derivations shared by the step-3 supplier
 * sections. Lifted verbatim from MaterialSuppliersSection / JointSuppliersCheckboxList
 * so those components stay under the size budget and the selection logic is
 * testable on its own.
 */

export type SelectedSupplier = {
  id: string;
  name: string;
  email: string;
  type: "existing" | "invited" | "email" | "potential";
  validated: boolean;
  isSelected: boolean;
  supplierData?: any;
};

/** Per-material variant (MaterialSuppliersSection). */
export const deriveMaterialSelectedSuppliers = (
  currentOrder: any,
  suppliers: BuyerSellerResponse[]
): SelectedSupplier[] => {
  const selectedSuppliers: SelectedSupplier[] = [];
  if (currentOrder?.suppliers) {
    currentOrder.suppliers.forEach((supplier: any) => {
      if (supplier.id && !supplier.emailsList) {
        const fullSupplierInfo = suppliers.find(
          (sup: any) => sup.seller.id === supplier.id
        );
        selectedSuppliers.push({
          id: supplier.id,
          name:
            supplier.companyName ||
            fullSupplierInfo?.seller?.company?.name ||
            `${fullSupplierInfo?.seller?.firstName || ""} ${fullSupplierInfo?.seller?.lastName || ""}`.trim() ||
            supplier.name ||
            "Unknown",
          email: supplier.email || fullSupplierInfo?.seller?.email || "",
          validated: supplier.validated,
          type: "existing",
          isSelected: true,
          supplierData: fullSupplierInfo || supplier,
        });
      } else if (supplier.emailsList) {
        const emails = String(supplier.emailsList)
          .split(":")
          .map((e) => e.trim())
          .filter(Boolean);
        emails.forEach((email) => {
          selectedSuppliers.push({
            id: `email:${supplier.emailsList}:${email}`,
            name: email,
            email,
            type: "email",
            validated: false,
            isSelected: true,
            supplierData: supplier,
          });
        });
      }
    });
  }
  return selectedSuppliers;
};

/** Joint variant (JointSuppliersCheckboxList) — de-duplicated across all lines. */
export const deriveJointSelectedSuppliers = (
  ordersToSubmit: any[]
): SelectedSupplier[] => {
  const selectedSuppliers: SelectedSupplier[] = [];
  const uniqueSelected = new Map();

  ordersToSubmit.forEach((order: any) => {
    order.suppliers?.forEach((supplier: any) => {
      if (supplier.id && !supplier.emailsList) {
        if (!uniqueSelected.has(supplier.id)) {
          uniqueSelected.set(supplier.id, supplier);
          selectedSuppliers.push({
            id: supplier.id,
            name: supplier.companyName || supplier.name || "Unknown",
            email: supplier.email || "",
            type: "existing",
            validated: supplier.validated || false,
            isSelected: true,
          });
        }
      } else if (supplier.emailsList) {
        const emails = String(supplier.emailsList)
          .split(":")
          .map((e) => e.trim())
          .filter(Boolean);
        emails.forEach((email) => {
          if (!uniqueSelected.has(email)) {
            uniqueSelected.set(email, supplier);
            selectedSuppliers.push({
              id: `email:${supplier.emailsList}:${email}`,
              name: email,
              email,
              validated: supplier.validated || false,
              type: "email",
              isSelected: true,
              supplierData: supplier,
            } as SelectedSupplier);
          }
        });
      }
    });
  });

  return selectedSuppliers;
};

/** Joint: the unique base supplier list from the grouped buyer-seller response. */
export const getUniqueJointSuppliers = (data: BuyerSellerResponse[][]) => {
  const uniqueSuppliers = new Map();
  data.forEach((group: BuyerSellerResponse[]) => {
    group.forEach((entry: BuyerSellerResponse) => {
      const supplier = entry.seller || entry;
      const supplierId = supplier?.id;
      if (supplier && supplierId) {
        if (!uniqueSuppliers.has(supplierId)) {
          uniqueSuppliers.set(supplierId, {
            ...supplier,
            validated: entry.validated || false,
          });
        }
      }
    });
  });
  return Array.from(uniqueSuppliers.values());
};
